/**
 * RegexQuestionParser - A utility class to parse questions from text using regex patterns
 * Supports multiple question formats including numbered questions with options
 */

class RegexQuestionParser {
  constructor() {
    // Common patterns for detecting questions
    this.questionPatterns = [
      /(?:^|\n)\s*(?:Q|Question)\s*(\d+)[\.\)]\s*(.+?)(?=\n\s*(?:Q|Question)\s*\d+[\.\)]|$)/is,
      /(?:^|\n)\s*(\d+)[\.\)]\s*(.+?)(?=\n\s*\d+[\.\)]|$)/is,
    ];

    // Common patterns for detecting options
    this.optionPatterns = [
      /^[\(\)]?\s*([A-D])[\.\)]\s*(.+?)(?=\n\s*[\(\)]?\s*[A-D][\.\)]|$)/gim,
      /^[\(\)]?\s*([a-d])[\.\)]\s*(.+?)(?=\n\s*[\(\)]?\s*[a-d][\.\)]|$)/gim,
      /^[\(\)]?\s*(\d+)[\.\)]\s*(.+?)(?=\n\s*[\(\)]?\s*\d+[\.\)]|$)/gim,
    ];

    // Patterns for detecting correct answers
    this.answerPatterns = [
      /(?:Answer|Ans|Correct|Key)[\s:]*([A-D])/i,
      /(?:Answer|Ans|Correct|Key)[\s:]*([a-d])/i,
    ];
  }

  /**
   * Parse questions from text
   * @param {string} text - The text to parse
   * @returns {Promise<{success: boolean, questions: Array, error?: string}>}
   */
  async parseQuestions(text) {
    try {
      if (!text || typeof text !== 'string' || text.trim().length === 0) {
        return {
          success: false,
          questions: [],
          error: 'Empty or invalid text provided'
        };
      }

      // Normalize text
      const normalizedText = this.normalizeText(text);
      
      // Extract questions
      const questions = this.extractQuestions(normalizedText);

      if (questions.length === 0) {
        return {
          success: false,
          questions: [],
          error: 'No questions detected in the text. The format might not be recognized.'
        };
      }

      return {
        success: true,
        questions: questions
      };
    } catch (error) {
      return {
        success: false,
        questions: [],
        error: error.message || 'An error occurred while parsing questions'
      };
    }
  }

  /**
   * Normalize text for parsing
   * @param {string} text - Raw text
   * @returns {string} - Normalized text
   */
  normalizeText(text) {
    return text
      .replace(/[\u00A0\u2000-\u200F\u2028-\u202F\u205F-\u206F\u3000]/g, ' ') // Replace various spaces
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n{3,}/g, '\n\n') // Reduce multiple newlines
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
  }

  /**
   * Extract questions from normalized text
   * @param {string} text - Normalized text
   * @returns {Array} - Array of question objects
   */
  extractQuestions(text) {
    const questions = [];
    
    // Try different question patterns
    for (const pattern of this.questionPatterns) {
      const matches = [...text.matchAll(new RegExp(pattern.source, pattern.flags))];
      
      if (matches.length > 0) {
        matches.forEach(match => {
          const questionText = match[2] || match[0];
          const questionNumber = match[1] || null;
          
          if (questionText && questionText.trim().length > 10) {
            const parsed = this.parseQuestionBlock(questionText, questionNumber);
            if (parsed) {
              questions.push(parsed);
            }
          }
        });
        
        if (questions.length > 0) {
          break; // Use the first pattern that finds questions
        }
      }
    }

    // If no questions found with patterns, try splitting by common delimiters
    if (questions.length === 0) {
      const fallbackQuestions = this.fallbackParse(text);
      questions.push(...fallbackQuestions);
    }

    return questions;
  }

  /**
   * Parse a single question block
   * @param {string} block - Question block text
   * @param {string|null} questionNumber - Question number if available
   * @returns {Object|null} - Parsed question object or null
   */
  parseQuestionBlock(block, questionNumber = null) {
    // Extract options
    const options = this.extractOptions(block);
    
    // Extract question text (remove options and answer indicators)
    let questionText = block;
    
    // Remove option markers
    questionText = questionText.replace(/^[\(\)]?\s*[A-Da-d][\.\)]\s*/gm, '');
    questionText = questionText.replace(/^[\(\)]?\s*\d+[\.\)]\s*/gm, '');
    
    // Remove answer indicators
    questionText = questionText.replace(/(?:Answer|Ans|Correct|Key)[\s:]*[A-Da-d]?/gi, '');
    
    questionText = questionText.trim();
    
    // Extract correct answer if available
    const correctAnswer = this.extractCorrectAnswer(block);
    
    // Validate that we have at least a question and some options
    if (questionText.length < 10) {
      return null;
    }

    return {
      question: questionText,
      options: options.length > 0 ? options : this.generateDefaultOptions(),
      correctAnswer: correctAnswer,
      questionNumber: questionNumber
    };
  }

  /**
   * Extract options from question block
   * @param {string} block - Question block text
   * @returns {Array} - Array of option strings
   */
  extractOptions(block) {
    const options = [];
    
    // Try each option pattern
    for (const pattern of this.optionPatterns) {
      const matches = [...block.matchAll(new RegExp(pattern.source, pattern.flags))];
      
      if (matches.length >= 2) {
        matches.forEach(match => {
          const optionText = (match[2] || match[0]).trim();
          if (optionText && optionText.length > 0 && !options.includes(optionText)) {
            options.push(optionText);
          }
        });
        
        if (options.length >= 2) {
          break; // Use the first pattern that finds enough options
        }
      }
    }

    // If no options found with patterns, try to find numbered or lettered options
    if (options.length === 0) {
      // Look for lines that might be options
      const lines = block.split('\n');
      const potentialOptions = [];
      
      lines.forEach(line => {
        const trimmed = line.trim();
        // Check if line looks like an option (starts with letter/number, not too long)
        if (trimmed.match(/^[A-Da-d0-9][\.\)]\s*/) && trimmed.length < 200) {
          const optionText = trimmed.replace(/^[A-Da-d0-9][\.\)]\s*/, '').trim();
          if (optionText && optionText.length > 0) {
            potentialOptions.push(optionText);
          }
        }
      });
      
      if (potentialOptions.length >= 2) {
        options.push(...potentialOptions.slice(0, 4));
      }
    }

    return options.slice(0, 4); // Limit to 4 options
  }

  /**
   * Extract correct answer from question block
   * @param {string} block - Question block text
   * @returns {string|null} - Correct answer letter (A, B, C, or D) or null
   */
  extractCorrectAnswer(block) {
    for (const pattern of this.answerPatterns) {
      const match = block.match(pattern);
      if (match && match[1]) {
        return match[1].toUpperCase();
      }
    }
    return null;
  }

  /**
   * Generate default options if none found
   * @returns {Array} - Array of empty option strings
   */
  generateDefaultOptions() {
    return ['', '', '', ''];
  }

  /**
   * Fallback parsing method when standard patterns don't work
   * @param {string} text - Normalized text
   * @returns {Array} - Array of question objects
   */
  fallbackParse(text) {
    const questions = [];
    
    // Split by double newlines or common separators
    const blocks = text.split(/\n\n+/);
    
    blocks.forEach((block, index) => {
      const trimmed = block.trim();
      
      // Skip very short blocks or blocks that look like headers
      if (trimmed.length < 20 || trimmed.match(/^(JEE|NEET|CAT|GATE|Section|Part)/i)) {
        return;
      }
      
      // Try to extract as a question
      const parsed = this.parseQuestionBlock(trimmed, String(index + 1));
      if (parsed && parsed.question.length >= 10) {
        questions.push(parsed);
      }
    });
    
    return questions;
  }
}

export default RegexQuestionParser;

